import { useEffect, useRef, useState } from "react";
import { tickBeep, endBeep } from "@/utils/timerSounds";
import { useTheme } from "@/context/ThemeContext";
import { serverNow } from "@/utils/serverClock";
import { ss, offsetTransform } from "@/utils/displayStyle";
import { useI18n } from "@/context/I18nContext";

/**
 * 3-2-1 visual pre-countdown.
 * Reads timer.pre_countdown_started_at (ISO UTC) and counts down 3 seconds.
 * Calls onComplete() when finished. Plays tick + end beeps.
 */
export const PreCountdown = ({ timer, onComplete, playSounds = true, screen = "circle_start" }) => {
  const { theme } = useTheme();
  const { t } = useI18n();
  const readyS = ss(theme, screen, "get_ready_text");
  const numS = ss(theme, screen, "pre_countdown_number", { color: theme?.pre_countdown_color });
  const [tick, setTick] = useState(0);
  const lastShownRef = useRef(null);
  const completedRef = useRef(false);

  useEffect(() => {
    completedRef.current = false;
    lastShownRef.current = null;
  }, [timer?.pre_countdown_started_at]);

  useEffect(() => {
    if (!timer || timer.status !== "pre_countdown") return;
    let raf;
    const loop = () => {
      setTick(t => t + 1);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    // Fallback for background/unfocused windows where rAF is throttled.
    const iv = setInterval(() => setTick(t => t + 1), 250);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(iv);
    };
  }, [timer?.status]);

  if (!timer || timer.status !== "pre_countdown" || !timer.pre_countdown_started_at) return null;

  const startedMs = new Date(timer.pre_countdown_started_at).getTime();
  const elapsed = serverNow() - startedMs;
  const totalMs = 3000;
  const remaining = Math.max(0, totalMs - elapsed);
  const num = Math.ceil(remaining / 1000); // 3, 2, 1, 0

  // Sounds
  if (playSounds && lastShownRef.current !== num) {
    const prev = lastShownRef.current;
    lastShownRef.current = num;
    if (prev !== null) {
      if (num === 0) endBeep();
      else if (num >= 1 && num <= 3 && prev > num) tickBeep();
    }
  }

  if (remaining <= 0 && !completedRef.current) {
    completedRef.current = true;
    if (onComplete) onComplete();
  }
  void tick;

  return (
    <div className="flex flex-col items-center justify-center gap-4" data-testid="pre-countdown">
      <div
        style={{
          color: readyS.color,
          fontSize: `${1.75 * (readyS.size || 1)}rem`,
          transform: offsetTransform(readyS),
          textShadow: readyS.textShadow,
        }}
      >
        {t('timer.preparing') || 'Get ready!'}
      </div>
      <div
        key={num}
        className="leading-none font-black animate-[pulse_0.5s_ease-out]"
        style={{
          fontVariantNumeric: "tabular-nums",
          color: numS.color,
          fontSize: `${20 * (numS.size || 1)}rem`,
          transform: offsetTransform(numS),
          textShadow: numS.textShadow,
        }}
        data-testid={`pre-countdown-${num}`}
      >
        {num > 0 ? num : "GO!"}
      </div>
    </div>
  );
};

export default PreCountdown;
